/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { differenceInCalendarDays, isSameDay } from "date-fns";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSelect from "@ui-components/Select/GenericSelect";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import { createLicenseV1, updateLicenseV1, fetchCustomersWithTenant, fetchPartnersWithTenant, fetchSubscriptionsForTenant } from "./request/request";
import { parseLicenseDate } from "./licenseDate";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { setSubscriptionId } from "@redux/slices/tenantSlice";

interface LicensePrefill {
  id?: number;
  tenantId?: number;
  serial?: string;
  description?: string;
  type?: string;
  // The table's text for each date, as `formatLicenseDate` rendered it.
  startdate?: string;
  enddate?: string;
  total?: number;
  activated?: number;
}

interface AddLicenseModalV1Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: () => void;
  prefill?: LicensePrefill | null;
}

// Helper function to format date as YYYY-MM-DD
const formatDateToYYYYMMDD = (date: Date | null): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * The day to send for an edited Start or End, or `undefined` to leave the
 * stored one alone. The PUT overwrites any date it receives, and resending the
 * prefilled one is how a description-only save moved the stored day back west
 * of UTC, or by months when it was read month-first (CTX-1438) — and logged a
 * date change in the audit either way. Only a date the user actually moved goes
 * out, as a calendar day like on create.
 */
const movedDay = (picked: Date | null, prefilled: Date | null): string | undefined => {
  if (!picked || (prefilled && isSameDay(picked, prefilled))) return undefined;
  return formatDateToYYYYMMDD(picked);
};

interface NewLicenseForm {
  tenant: string;
  licenseType: string;
  startDate: Date | null;
  endDate: Date | null;
  description: string;
  numDevices: string;
}

type NewLicenseErrors = Partial<Record<keyof NewLicenseForm, string>>;

/**
 * What keeps a new license from being sent, by field. Checked here because
 * nothing else does: `GenericInput`'s `required` only draws the asterisk, and
 * the backend answers a missing field with a 200 that read as "License Created
 * Successfully" with no license made (CTX-1439).
 */
const validateNewLicense = (form: NewLicenseForm): NewLicenseErrors => {
  const errors: NewLicenseErrors = {};
  if (!form.tenant) errors.tenant = "Select a customer.";
  if (!form.licenseType) errors.licenseType = "Select a license type.";
  if (!form.startDate) errors.startDate = "Pick a start date.";
  if (!form.endDate) errors.endDate = "Pick an end date.";
  if (form.startDate && form.endDate && differenceInCalendarDays(form.endDate, form.startDate) < 0) {
    errors.endDate = "End Date can't be before Start Date.";
  }
  if (!form.description.trim()) errors.description = "Enter a description.";
  const devices = Number(form.numDevices);
  if (!Number.isInteger(devices) || devices < 1) {
    errors.numDevices = "Enter a number of devices greater than 0.";
  }
  return errors;
};

/** The error line under a field with no error state of its own, styled like `GenericInput`'s. */
const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="mt-1 text-xs text-red-500">{message}</p> : null;

const AddLicenseModalV1: React.FC<AddLicenseModalV1Props> = ({
  isOpen,
  onClose,
  onCreate,
  prefill,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector((state) => state.userProfile);
  const isGlobalAdmin = userProfile.TenantType === "GLOBAL_ADMIN";
  const { tenantId: currentTenantId } = useAppSelector(state => state.selection);
  const [tenant, setTenant] = useState("");
  const [description, setDescription] = useState("");
  const [licenseType, setLicenseType] = useState("CTXR-TC-AWS-S-1Y");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [numDevices, setNumDevices] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entities, setEntities] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [selectedCustomerSubscriptionId, setSelectedCustomerSubscriptionId] = useState<string | null>(null);
  const [isLoadingSubscriptionId, setIsLoadingSubscriptionId] = useState(false);
  // Field errors stay hidden until the first Create, then follow every change.
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  // Check if we're in edit mode (prefill has an id)
  const isEditMode = prefill?.id !== undefined;

  // Only the create form is checked: an edit sends just what it changes.
  const newLicenseErrors: NewLicenseErrors = isEditMode
    ? {}
    : validateNewLicense({ tenant, licenseType, startDate, endDate, description, numDevices });
  const fieldErrors = showFieldErrors ? newLicenseErrors : {};
  
  // License types for v1
  const licenseTypes = [
    { value: "CTXR-TC-AWS-S-3Y", label: "CTXR-TC-AWS-S-3Y" },
    { value: "CTXR-TC-AWS-M-3Y", label: "CTXR-TC-AWS-M-3Y" },
    { value: "CTXR-TC-AWS-L-3Y", label: "CTXR-TC-AWS-L-3Y" },
    { value: "CTXR-TC-AWS-S-2Y", label: "CTXR-TC-AWS-S-2Y" },
    { value: "CTXR-TC-AWS-M-2Y", label: "CTXR-TC-AWS-M-2Y" },
    { value: "CTXR-TC-AWS-L-2Y", label: "CTXR-TC-AWS-L-2Y" },
    { value: "CTXR-TC-AWS-S-1Y", label: "CTXR-TC-AWS-S-1Y" },
    { value: "CTXR-TC-AWS-M-1Y", label: "CTXR-TC-AWS-M-1Y" },
    { value: "CTXR-TC-AWS-L-1Y", label: "CTXR-TC-AWS-L-1Y" },
  ];

  // The dates the edit form opened with, so saving can tell a moved date from
  // an untouched one (see `movedDay`).
  const prefilledStartDate = useMemo(() => parseLicenseDate(prefill?.startdate), [prefill]);
  const prefilledEndDate = useMemo(() => parseLicenseDate(prefill?.enddate), [prefill]);

  // Clear error when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setShowFieldErrors(false);
      if (prefill) {
        // Prefill form with existing license data
        if (prefill.description) setDescription(prefill.description);
        if (prefill.type) setLicenseType(prefill.type);
        if (prefill.startdate) setStartDate(prefilledStartDate);
        if (prefill.enddate) setEndDate(prefilledEndDate);
        if (prefill.total !== undefined) setNumDevices(String(prefill.total));
        if (prefill.tenantId) setTenant(String(prefill.tenantId));
      } else {
        // Reset form when opening for new license
        setDescription("");
        setLicenseType("CTXR-TC-AWS-S-1Y");
        setStartDate(null);
        setEndDate(null);
        setNumDevices("");
        setTenant("");
        setSelectedCustomerSubscriptionId(null);
      }
    }
  }, [isOpen, prefill, prefilledStartDate, prefilledEndDate]);

  // Fetch subscription ID for the selected customer/tenant
  // Uses the same endpoint as HA clusters: /v1/suscription?tenantId=...
  const fetchCustomerSubscriptionId = useCallback(async (customerTenantId: number) => {
    if (!customerTenantId) {
      setSelectedCustomerSubscriptionId(null);
      return;
    }

    setIsLoadingSubscriptionId(true);
    setError(null); // Clear previous errors
    try {
      const token = await getAccessTokenSilently();
      const response = await fetchSubscriptionsForTenant(token, customerTenantId);
      
      // Response should be an array of subscriptions: [{ id: number, purchaseOrden: string }, ...]
      const subscriptions = response.data || [];
      
      if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
        setSelectedCustomerSubscriptionId(null);
        setError("No subscriptions found for the selected customer. The customer needs to have a subscription before creating a license.");
        return;
      }
      
      // Use the first subscription's ID (same pattern as HA clusters)
      // Convert to string as the API expects string | number
      const firstSubscription = subscriptions[0];
      const subscriptionId = String(firstSubscription.id);
      
      if (!subscriptionId || subscriptionId === "undefined" || subscriptionId === "null") {
        setSelectedCustomerSubscriptionId(null);
        setError("Invalid subscription ID for the selected customer.");
      } else {
        setSelectedCustomerSubscriptionId(subscriptionId);
      }
    } catch (err: any) {
      console.error("Error fetching subscriptions for customer:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Unknown error";
      setSelectedCustomerSubscriptionId(null);
      setError(`Failed to fetch subscriptions: ${errorMessage}`);
    } finally {
      setIsLoadingSubscriptionId(false);
    }
  }, [getAccessTokenSilently]);

  const fetchEntitiesData = useCallback(async () => {
    setIsLoadingEntities(true);
    try {
      const token = await getAccessTokenSilently();
      let combined: { value: string; label: string }[] = [];
      // Always fetch customers
      const customersRes = await fetchCustomersWithTenant(token, currentTenantId);
      const customers = customersRes.data.data.map((customer: any) => ({
        value: customer.id.toString(),
        label: `Customer: ${customer.nameDisplay || customer.name}`
      }));
      combined = [...customers];
      if (isGlobalAdmin) {
        const partnersRes = await fetchPartnersWithTenant(token);
        const partners = partnersRes.data.data.map((partner: any) => ({
          value: partner.id.toString(),
          label: `Partner: ${partner.nameDisplay || partner.name}`
        }));
        combined = [...customers, ...partners];
      }
      setEntities(combined);
      // Auto-select first customer if none selected and not in edit mode
      if (combined.length > 0 && !tenant && !isEditMode) {
        const firstCustomerId = combined[0].value;
        setTenant(firstCustomerId);
        // Fetch subscription ID for the first customer
        const customerTenantId = parseInt(firstCustomerId);
        if (!isNaN(customerTenantId)) {
          fetchCustomerSubscriptionId(customerTenantId);
        }
      }
    } catch (err) {
      console.error('Error fetching entities:', err);
      setError('Failed to load tenants');
    } finally {
      setIsLoadingEntities(false);
    }
  }, [getAccessTokenSilently, currentTenantId, isGlobalAdmin, tenant, isEditMode, fetchCustomerSubscriptionId]);

  // Fetch customers/partners when modal opens
  useEffect(() => {
    if (isOpen && currentTenantId) {
      fetchEntitiesData();
    }
  }, [isOpen, currentTenantId, fetchEntitiesData]);

  // Fetch subscription ID when customer selection changes
  useEffect(() => {
    if (isOpen && tenant && !isEditMode) {
      const customerTenantId = parseInt(tenant);
      if (!isNaN(customerTenantId)) {
        fetchCustomerSubscriptionId(customerTenantId);
      }
    }
  }, [tenant, isOpen, isEditMode, fetchCustomerSubscriptionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (Object.keys(newLicenseErrors).length > 0) {
      setShowFieldErrors(true);
      return;
    }
    setIsLoading(true);

    try {
      // Get the access token
      const token = await getAccessTokenSilently();

      if (isEditMode && prefill?.id) {
        // Edit mode: Update existing license using v1 API
        const payload = {
          type: licenseType,
          total: parseInt(numDevices) || 0,
          description: description.trim(),
          enddate: movedDay(endDate, prefilledEndDate),
          startdate: movedDay(startDate, prefilledStartDate),
        };

        await updateLicenseV1(token, prefill.id, payload, currentTenantId);
      } else {
        // Create mode: Create new license using v1 API
        // Use subscription ID from the selected customer
        const subscriptionIdToUse = selectedCustomerSubscriptionId;
        
        if (!subscriptionIdToUse || subscriptionIdToUse === "-1" || subscriptionIdToUse === null) {
          setError("Subscription ID is required for the selected customer. Please wait for it to load or try again.");
          setIsLoading(false);
          return;
        }
        
        // Ensure subscriptionId is a string, not an object
        const subscriptionIdString = typeof subscriptionIdToUse === "string" 
          ? subscriptionIdToUse 
          : String(subscriptionIdToUse);
        
        if (subscriptionIdString === "-1" || subscriptionIdString === "null" || subscriptionIdString === "") {
          setError("Invalid subscription ID for the selected customer. Please try selecting a different customer.");
          setIsLoading(false);
          return;
        }
        
        const payload = {
          tenantId: parseInt(tenant),
          subscriptionId: subscriptionIdString,
          description: description.trim(),
          type: licenseType,
          startdate: formatDateToYYYYMMDD(startDate),
          enddate: formatDateToYYYYMMDD(endDate),
          total: numDevices
        };

        const { data } = await createLicenseV1(token, payload, currentTenantId);
        // A 200 can still be a rejection; only a serial means the license exists.
        if (!data.subid) {
          setError(data.error ?? "The license was not created. Try again.");
          return;
        }
      }
      
      // Call the onCreate callback
      onCreate();
      
      // Close the modal on success
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || (isEditMode ? "Failed to update license" : "Failed to create license"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} title={isEditMode ? "Edit License" : "Add License"} size="lg">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {error && (
          <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        {!isEditMode ? (
          <div>
            <GenericSelect
              label="Customer"
              options={entities}
              onSelect={(value) => {
                setError(null); // Clear any previous errors
                setTenant(value);
                setSelectedCustomerSubscriptionId(null); // Clear previous subscription ID
                // Fetch subscription ID for the selected customer
                const customerTenantId = parseInt(value);
                if (!isNaN(customerTenantId)) {
                  fetchCustomerSubscriptionId(customerTenantId);
                }
              }}
              defaultValue={tenant}
              placeholder={isLoadingEntities ? "Loading tenants..." : "Select tenant"}
            />
            <FieldError message={fieldErrors.tenant} />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-semantic-text-primary mb-1">Customer</label>
            <div className="px-3 py-2 bg-semantic-bg-secondary border border-semantic-border-secondary rounded text-semantic-text-secondary">
              {entities.find(e => e.value === tenant)?.label || tenant || "N/A"}
            </div>
          </div>
        )}
        {!isEditMode ? (
          <div>
            <GenericSelect
              label="License Type"
              options={licenseTypes}
              onSelect={setLicenseType}
              defaultValue={licenseType}
              placeholder="Select license type"
            />
            <FieldError message={fieldErrors.licenseType} />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-semantic-text-primary mb-1">License Type</label>
            <div className="px-3 py-2 bg-semantic-bg-secondary border border-semantic-border-secondary rounded text-semantic-text-secondary">
              {licenseTypes.find(lt => lt.value === licenseType)?.label || licenseType || "N/A"}
            </div>
          </div>
        )}

        <div>
          <GenericDateTimeRangePicker
            label="Start Date"
            value={{ start: startDate, end: null }}
            onChange={(val) => setStartDate(val.start)}
            singleDate
          />
          <FieldError message={fieldErrors.startDate} />
        </div>
        <div>
          <GenericDateTimeRangePicker
            label="End Date"
            value={{ start: endDate, end: null }}
            onChange={(val) => setEndDate(val.start)}
            singleDate
          />
          <FieldError message={fieldErrors.endDate} />
        </div>
        <GenericInput
          label="Description"
          value={description}
          onChange={setDescription}
          placeholder="Description"
          required
          hasError={!!fieldErrors.description}
          helperText={fieldErrors.description}
        />

        <GenericInput
          label="Number of Devices"
          value={numDevices}
          onChange={setNumDevices}
          type="number"
          placeholder="Number of devices"
          required
          hasError={!!fieldErrors.numDevices}
          helperText={fieldErrors.numDevices}
        />
        {!isEditMode && isLoadingSubscriptionId && (
          <div className="md:col-span-2 text-sm text-semantic-text-secondary">
            Loading subscription ID for selected customer...
          </div>
        )}
        <div className="md:col-span-2 flex justify-end mt-4">
          <GenericButton
            label={isLoading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update License" : "Create License")}
            type="submit"
            variant="brand"
            size="md"
            disabled={isLoading || isLoadingEntities || (!isEditMode && isLoadingSubscriptionId)}
            loading={isLoading}
          />
        </div>
      </form>
    </GenericModal>
  );
};

export default AddLicenseModalV1;
